import { useEffect, useState } from 'react';

import { twMerge } from 'tailwind-merge';

import { useRutilusMutation } from '../../api/graphql/useRutilusMutation';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { reportMykissClientError } from '../../helpers/errorHandling/client';
import { useUserInfo } from '../../helpers/hooks/useUserInfo';
import { EventAddLike, EventMykissLike } from '../../interfaces/events';
import { LikePost, LikePostActionEnum, LikePostVariables } from '../../interfaces/graphql';
import { likePostMutation } from '../../mutations/rutilus/LikePost';
import { Button } from '../Clickables/Buttons/Button';
import { LikeIcon } from '../Icons/LikeIcon';

import { sendLikeEvent } from './sendLikeEvent';

export type LikeType = EventAddLike['properties']['object_type'];

type LabelPosition = 'right' | 'bottom';

interface IProps {
  className?: string;
  hideNumber?: boolean;
  iconSize?: number;
  isLiked: boolean;
  labelPosition?: LabelPosition;
  likesInfoClassName?: string;
  numberOfLikes: number;
  onLikesButtonClick?: () => void;
  onLikesInfoClick?: () => void;
  onlyNumber?: boolean;
  onNotLoggedIn?: () => void;
  postId: string; // Post or Catch
  source: EventMykissLike['properties']['source'];
  type: LikeType;
}

const getLikesString = (count: number): string => (count === 1 ? '1 like' : `${count} likes`);

export const LikeButton = ({
  className,
  hideNumber,
  iconSize,
  isLiked: initialIsLiked,
  labelPosition = 'right',
  likesInfoClassName,
  numberOfLikes,
  onLikesButtonClick,
  onLikesInfoClick,
  onlyNumber,
  onNotLoggedIn: handleNotLoggedIn,
  postId,
  source,
}: IProps) => {
  const { currentPage: page } = useAnalytics();
  const { isLoggedIn } = useUserInfo();
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likes, setNumberOfLikes] = useState(numberOfLikes);
  const [toggleLikeMutation] = useRutilusMutation<LikePost, LikePostVariables>(likePostMutation);

  useEffect(() => {
    setIsLiked(initialIsLiked);
    setNumberOfLikes(numberOfLikes);
  }, [initialIsLiked, numberOfLikes]);

  const toggleLike = (wasLiked: boolean): void => {
    if (!isLoggedIn) {
      handleNotLoggedIn?.();
      return;
    }
    if (wasLiked) {
      toggleLikeMutation({
        variables: {
          input: { attributes: { likeAction: LikePostActionEnum.LIKE, postExternalId: postId } },
        },
      }).catch(reportMykissClientError);
      sendLikeEvent({ page, source, type: 'add' });
    } else {
      toggleLikeMutation({
        variables: {
          input: { attributes: { likeAction: LikePostActionEnum.UNLIKE, postExternalId: postId } },
        },
      }).catch(reportMykissClientError);
      sendLikeEvent({ page, source, type: 'remove' });
    }
    setNumberOfLikes(likes + (wasLiked ? 1 : -1));
    setIsLiked(wasLiked);
  };

  return (
    <div
      className={twMerge(
        'flex',
        !hideNumber && labelPosition === 'right' ? 'items-center' : 'flex-col',
        className,
      )}
    >
      <Button
        variant="textPrimary"
        onClick={() => {
          if (onLikesButtonClick) {
            onLikesButtonClick();
          }
          toggleLike(!isLiked);
        }}
        style={{ outline: 'none' }}
        data-testid="LikeButton"
      >
        <div
          className={twMerge(
            'flex justify-start',
            !hideNumber && (labelPosition === 'right' ? 'mr-3' : 'mb-3'),
          )}
        >
          <LikeIcon title="like button icon" size={iconSize} isLiked={isLiked} />
        </div>
      </Button>
      {!hideNumber && (
        <Button
          className={twMerge('p-0!', likesInfoClassName)}
          onClick={onLikesInfoClick}
          variant="buttonNone"
        >
          {onlyNumber ? likes : getLikesString(likes)}
        </Button>
      )}
    </div>
  );
};
